import type { AgentDockItem } from "@/components/cursors/use-agent-dock-items";
import { DenkerThinkingLogo } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { DenkerLogo } from "@/components/ui/logo";
import { forwardRef } from "react";
import type { CompactVoicePhase } from "./use-compact-voice-status";
import { translate } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

const PHASE_LABEL_KEY: Partial<
  Record<
    CompactVoicePhase,
    | "logoStarting"
    | "logoSending"
    | "logoSent"
    | "logoSpeaking"
    | "logoLive"
    | "logoListening"
  >
> = {
  starting: "logoStarting",
  sending: "logoSending",
  sent: "logoSent",
  speaking: "logoSpeaking",
  live: "logoLive",
  listening: "logoListening",
};

function statusButtonLabel(
  item: AgentDockItem | null,
  phase: CompactVoicePhase,
  voiceActive: boolean,
): string {
  const phaseKey = !voiceActive ? null : PHASE_LABEL_KEY[phase];
  const name = item?.cursor.name ?? "";
  const actionLabel = !item
    ? translate(compactMessages, "recentChats")
    : translate(
        compactMessages,
        item.isError
          ? "agentStatusError"
          : item.isRunning
            ? "agentStatusThinking"
            : item.hasUnseenResult
              ? "agentStatusNewResult"
              : "agentStatusOpen",
        { name },
      );
  return phaseKey
    ? `${translate(compactMessages, phaseKey)} ${actionLabel}`
    : actionLabel;
}

function CompactDenkerStatusLogo({
  item,
  phase,
  voiceActive,
}: {
  item: AgentDockItem | null;
  phase: CompactVoicePhase;
  voiceActive: boolean;
}) {
  const showSentIcon = voiceActive && (phase === "sending" || phase === "sent");
  const showThinkingLogo = item?.isRunning && !voiceActive;
  return (
    <>
      {showSentIcon ? (
        <Icons.ArrowUp
          aria-hidden="true"
          className="relative z-[1] h-4 w-4 stroke-[2.25]"
          data-testid="desktop-compact-denker-sent-icon"
        />
      ) : showThinkingLogo ? (
        <DenkerThinkingLogo
          className="relative z-[1] h-5 w-5"
          testId="desktop-compact-denker-thinking-logo"
        />
      ) : (
        <DenkerLogo
          variant="symbol"
          color="currentColor"
          height={15}
          className="relative z-[1]"
        />
      )}
    </>
  );
}

const CompactDenkerStatusButton = forwardRef<
  HTMLButtonElement,
  {
    item: AgentDockItem | null;
    expanded: boolean;
    menuOpen: boolean;
    onOpen: () => void;
    phase: CompactVoicePhase;
    voiceActive: boolean;
    live?: boolean;
  }
>(function CompactDenkerStatusButton(
  { item, expanded, menuOpen, onOpen, phase, voiceActive, live = false },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className="absolute left-0 top-0 z-[3] grid h-9 max-h-9 min-h-9 w-9 min-w-9 max-w-9 cursor-[inherit] touch-none shrink-0 aspect-square place-items-center rounded-full text-primary focus-visible:outline-none focus-visible:ring-2"
      data-testid="desktop-compact-denker-logo"
      data-compact-drag-handle="true"
      data-agent-status={item ? "true" : undefined}
      data-agent-id={item?.cursor.agentId}
      data-compact-state={phase}
      aria-expanded={menuOpen}
      aria-haspopup="menu"
      aria-label={statusButtonLabel(item, phase, voiceActive)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
      onFocus={onOpen}
      onPointerEnter={onOpen}
      tabIndex={expanded ? 0 : -1}
    >
      <CompactDenkerStatusLogo
        item={item}
        phase={phase}
        voiceActive={voiceActive}
      />
      {live && voiceActive && (
        <Icons.LiveWaveform
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1 right-1 z-[2] h-2 w-2 text-primary"
          data-testid="desktop-compact-live-marker"
        />
      )}
    </button>
  );
});

export { CompactDenkerStatusButton };
